import { useEffect } from 'react'
import { get } from 'lodash'

import { ACTION_CODE, EVENT_CODE, MAIN_OBJECT_TYPE_X, TIME_LINE_MODE } from '@constants'
import { GETVariableDTO, ObjectDataRecord, TimeLineDTOType } from '@types'

import useModifyDataAfterLoad from '../../../hooks/actions/useModifyDataAfterLoad'
import {
  convertApiResponseToTimeLineGridData,
  getFilteredColumnsObjectDataByObjectXData,
  getKeyFieldY,
  getObjectXLinkToObjectYKey,
  isGroupTitleRow,
} from '../helpers'

import { useGetDataFromColumnsObject } from './useGetDataFromColumnsObject'
import { useGetDataFromObjectXCommand } from './useGetDataFromObjectXCommand'
import { useGetDataFromObjectYCommand } from './useGetDataFromObjectYCommand'
import { RefreshTotalsParams } from './useGetTotals'
import { useHandlers } from './useHandlers'

type UseObjectYDataParams = {
  config: TimeLineDTOType
  refreshTotals: (params: RefreshTotalsParams) => void
} & ReturnType<typeof useHandlers>

type UseObjectYDataReturnType = {
  totalPages: number
  totalCountElements: number
  refresh: () => void
  onReadMore?: () => void
}

export const useGetDataByMainObjectX = (props: UseObjectYDataParams): UseObjectYDataReturnType => {
  const {
    config,
    refreshTotals,
    handlers: {
      handleChangeTimeLineGridData,
      handleChangeIsLoadingTimeLineGridData,
      handleChangeIsLoadingColumnsObjectData,
      handleChangeVariables,
      handleExecuteActions,
      handleChangeColumnsObjectData,
    },
    state: {
      timeLineGridData,
      dateIntervals,
      sortModel,
      objectXSARSQLFilter,
      objectYSARSQLFilter,
      objectXColumnsObjectSARSQLFilter,
      objectXSAFiltersCriteria,
      objectYSAFiltersCriteria,
      objectXColumnsObjectSAFiltersCriteria,
      isUseMainObjectXBySAFilter,
      isFilterColumnsObjectByObjectXFilter,
    },
    data: { startDate, endDate, objectYActionsObject, isNeedWaitFirstLoadWithSA },
  } = props

  const isLoadDataByObjectX =
    (config.mainObject?.objectType === MAIN_OBJECT_TYPE_X || isUseMainObjectXBySAFilter) &&
    !isNeedWaitFirstLoadWithSA

  const { getDataFromObjectXCommand } = useGetDataFromObjectXCommand()
  const { getDataFromObjectYCommand } = useGetDataFromObjectYCommand()
  const { getDataFromColumnsObject } = useGetDataFromColumnsObject()

  useEffect(() => {
    if (isLoadDataByObjectX) {
      getData()
    }
  }, [dateIntervals, sortModel, objectXSAFiltersCriteria])

  const { modifyData } = useModifyDataAfterLoad()

  const getObjectYDataByObjectXData = (
    objectXData: ObjectDataRecord[],
    columnsObjectData: ObjectDataRecord[] = []
  ) => {
    if (!objectXData?.length) {
      convertApiResponseToTimeLineGridData({
        config,
        dateIntervals,
        objectXData,
        objectYData: [],
        keyFieldY: getKeyFieldY(config),
        handleChangeColumnsObjectData,
        handleChangeTimeLineGridData,
        isInitialLoad: true,
        columnsObjectData,
        objectXSAFiltersCriteria,
      })

      handleChangeIsLoadingTimeLineGridData(false)

      return
    }

    const linkToObjectYPath = getObjectXLinkToObjectYKey(config.objectX.linkToObjectY)

    const objectYIdsSet = new Set()

    objectXData.forEach(item => {
      objectYIdsSet.add(get(item, linkToObjectYPath))
    })

    getDataFromObjectYCommand({
      config,
      escapedKeyFieldValues: Array.from(objectYIdsSet),
      saRSQLFilter: objectYSARSQLFilter,
      sort: sortModel,
    })
      .then(objectYData => {
        return modifyData(objectYActionsObject, objectYData)
      })
      .then(objectYData => {
        convertApiResponseToTimeLineGridData({
          config,
          dateIntervals,
          objectXData,
          objectYData,
          keyFieldY: getKeyFieldY(config),
          handleChangeColumnsObjectData,
          handleChangeTimeLineGridData,
          isInitialLoad: true,
          columnsObjectData,
          objectXSAFiltersCriteria,
        })
      })
      .finally(() => handleChangeIsLoadingTimeLineGridData(false))
  }

  const getData = async () => {
    let variables: Record<string, GETVariableDTO> = {}

    const actionsData = await handleExecuteActions({
      actions: config.actions,
      srcObj: { startDate, endDate },
      eventCode: EVENT_CODE.BEFORE_LOAD_DATA,
    })

    actionsData.forEach(item => {
      if (item.status === 'fulfilled') {
        if (item.value?.actionCode === ACTION_CODE.SET_VARIABLE_VALUE) {
          variables = { ...variables, ...item.value.value }
        }
      }
    })

    handleChangeVariables(variables)

    handleChangeIsLoadingTimeLineGridData(true)

    const promises = [
      getDataFromObjectXCommand({
        config,
        startDate,
        endDate,
        objectXSARSQLFilter,
        objectYSAFiltersCriteria,
        objectXColumnsObjectSAFiltersCriteria,
      }),
    ]

    if (config.mode === TIME_LINE_MODE.OBJECT) {
      handleChangeIsLoadingColumnsObjectData(true)
      promises.push(
        getDataFromColumnsObject({ config, rsqlFilter: objectXColumnsObjectSARSQLFilter })
      )
    }

    Promise.all(promises)
      .then(result => {
        if (config.mode === TIME_LINE_MODE.OBJECT) {
          handleChangeIsLoadingColumnsObjectData(false)

          if (result[1].length && result[0].length) {
            const totalsColumnsObjectData = isFilterColumnsObjectByObjectXFilter
              ? getFilteredColumnsObjectDataByObjectXData({
                  config,
                  columnsObjectData: result[1],
                  objectXData: result[0],
                })
              : result[1]

            refreshTotals({
              columnsObjectData: totalsColumnsObjectData,
              objectXSAFiltersCriteria,
              objectYSAFiltersCriteria,
              objectXColumnsObjectSAFiltersCriteria,
            })
          }
        }

        getObjectYDataByObjectXData(result[0], result[1])
      })
      .catch(() => {
        if (config.mode === TIME_LINE_MODE.OBJECT) {
          handleChangeIsLoadingColumnsObjectData(false)
        }
        handleChangeIsLoadingTimeLineGridData(false)
      })
  }

  return {
    totalPages: 1,
    totalCountElements: timeLineGridData.filter(item => !isGroupTitleRow(item.id)).length,
    refresh: getData,
  }
}
